import { Button, Icon } from '@zoom/components';
import classnames from 'classnames';
import { isValid, parse } from 'date-fns';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Field, FieldMetaState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import PerfectScrollbar from 'react-perfect-scrollbar';
import TimePicker from 'react-time-picker';
import { Dropdown, DropdownMenu, DropdownToggle, Label } from 'reactstrap';

import { TimePickerOption } from './TimePickerOption';
import {
  ONE_HOUR_IN_SECONDS,
  ONE_MINUTE_IN_SECONDS,
  TIME_FORMAT_24_HOURS,
  convertTimeFromNumberToStringValue,
  formatTimeToTwoDigitString,
} from '../../utils/time';
import { TValidator } from '../../utils/validations/types';
import { Separator } from '../layout/Separator';

import type { LooseValue } from 'react-time-picker/dist/cjs/shared/types';

const HEIGHT_OF_DROPDOWN_ITEM = 29;

interface OptionType {
  value: number;
  label: string;
}

type TimeDurationPickerProps = {
  alignMenuToEnd?: boolean;
  className: string;
  'data-test-id'?: string;
  disabled?: boolean;
  inputName: string;
  label: React.ReactNode;
  max?: number;
  meta: FieldMetaState<'input'>;
  min?: number;
  onBlur?: (event?: any) => void;
  onFocus?: (event?: any) => void;
  onSelect: (value?: number) => void;
  onToggle?: (isOpen?: boolean) => void;
  value?: number;
  stepMinutes?: number;
  stepHours?: number;
  minSecOption?: boolean;
};

export const TimeDurationPicker: React.FC<TimeDurationPickerProps> = ({
  alignMenuToEnd,
  className,
  disabled,
  'data-test-id': dataTestId,
  inputName,
  label,
  max = ONE_HOUR_IN_SECONDS * 24, // 24:00 in seconds
  meta: { active, dirty, modified, valid },
  min = 0, // 00:00 in seconds
  onBlur,
  onFocus,
  onSelect,
  onToggle = () => null,
  value,
  stepMinutes = 1, // display only minutes divided by this value
  stepHours = 1, // display only hours divided by this value
  minSecOption = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setFocused] = useState(false);
  const [tempHourValue, setTempHourValue] = useState<number | undefined>(undefined);
  const [tempMinuteValue, setTempMinuteValue] = useState<number | undefined>(undefined);

  const [hoursContainerRef, setHoursContainerRef] = useState<HTMLElement | null>(null);
  const [minutesContainerRef, setMinutesContainerRef] = useState<HTMLElement | null>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);

  const isValueEmpty = value === undefined || (typeof value === 'string' && value === '');
  const containInvalidTempValues = tempHourValue === -1 || tempMinuteValue === -1;

  const valueHours = !isValueEmpty ? Math.floor(value / ONE_HOUR_IN_SECONDS) : 0;
  const valueMinutes = !isValueEmpty
    ? Math.floor((value - valueHours * ONE_HOUR_IN_SECONDS) / ONE_MINUTE_IN_SECONDS)
    : 0;

  const handleToggle = () => {
    if (disabled) {
      return;
    }

    if (isOpen) {
      setTempHourValue(undefined);
      setTempMinuteValue(undefined);
      onBlur?.();
      setFocused(false);
    }
    setIsOpen(!isOpen);
    onToggle(!isOpen);
  };

  useEffect(() => {
    if (isOpen) {
      const hours = valueHours || tempHourValue;
      const minutes = valueMinutes || tempMinuteValue;

      if (hoursContainerRef && hours) {
        // scroll to selected hour which should be placed in the center of container
        hoursContainerRef.scrollTop = (hours - 3) * HEIGHT_OF_DROPDOWN_ITEM || 0;
      }
      if (minutesContainerRef && minutes) {
        // scroll to selected minute which should be placed in the center of container
        minutesContainerRef.scrollTop = (minutes - 3) * HEIGHT_OF_DROPDOWN_ITEM || 0;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hoursContainerRef, minutesContainerRef, valueHours, valueMinutes]);

  const { t } = useTranslation();

  const hoursOptions = useMemo(() => {
    const minHour = min / ONE_HOUR_IN_SECONDS - (min % ONE_HOUR_IN_SECONDS);
    const maxHour = max / ONE_HOUR_IN_SECONDS - (max % ONE_HOUR_IN_SECONDS);

    const hourOptionsToReturn: OptionType[] = [];

    for (let hour = minHour; hour < maxHour; hour += 1) {
      if (hour % stepHours !== 0) {
        // eslint-disable-next-line no-continue
        continue;
      }

      hourOptionsToReturn.push({
        value: hour,
        label: formatTimeToTwoDigitString(hour),
      });
    }

    return hourOptionsToReturn;
  }, [min, max, stepHours]);

  const minutesOptions = useMemo(() => {
    const minHour = min / ONE_HOUR_IN_SECONDS - (min % ONE_HOUR_IN_SECONDS);
    const maxHour = max / ONE_HOUR_IN_SECONDS - (max % ONE_HOUR_IN_SECONDS);

    let minMinute = 0;
    let maxMinute = ONE_MINUTE_IN_SECONDS;

    if (tempHourValue === minHour) {
      minMinute =
        (min - minHour * ONE_HOUR_IN_SECONDS) / ONE_MINUTE_IN_SECONDS -
        ((min - minHour * ONE_HOUR_IN_SECONDS) % ONE_MINUTE_IN_SECONDS);
    }
    if (tempHourValue === maxHour) {
      const maxMinuteInMaxHour =
        (max - maxHour * ONE_HOUR_IN_SECONDS) / ONE_MINUTE_IN_SECONDS -
        ((max - maxHour * ONE_HOUR_IN_SECONDS) % ONE_MINUTE_IN_SECONDS);

      if (maxMinuteInMaxHour !== 0) {
        maxMinute = maxMinuteInMaxHour;
      }
    }

    const minuteOptionsToReturn: OptionType[] = [];

    for (let minute = minMinute; minute < maxMinute; minute += 1) {
      if (minute % stepMinutes !== 0) {
        // eslint-disable-next-line no-continue
        continue;
      }

      minuteOptionsToReturn.push({
        value: minute,
        label: formatTimeToTwoDigitString(minute),
      });
    }

    return minuteOptionsToReturn;
  }, [min, max, tempHourValue, stepMinutes]);

  /**
   * @returns [nextNearestHourOption, nextNearestMinuteOption]
   */
  const getNearestValidOptions = (hoursToCheck: number, minutesToCheck: number): [number, number] => {
    return [Math.ceil(hoursToCheck / stepHours) * stepHours, Math.ceil(minutesToCheck / stepMinutes) * stepMinutes];
  };

  const handleClearTempValues = () => {
    setTempHourValue(undefined);
    setTempMinuteValue(undefined);
    setIsOpen(false);
  };

  const handleSubmitTempValues = () => {
    if (tempHourValue !== undefined || tempMinuteValue !== undefined) {
      const [newHours, newMinutes] = getNearestValidOptions(
        tempHourValue ?? valueHours,
        tempMinuteValue ?? valueMinutes
      );
      onSelect(newHours * ONE_HOUR_IN_SECONDS + newMinutes * ONE_MINUTE_IN_SECONDS);
    }
    handleClearTempValues();
  };

  const handleSelectHour = (hour: number, e: React.MouseEvent<HTMLElement>) => {
    if ((e && e.nativeEvent.detail && e.currentTarget.contains(document.activeElement)) || tempHourValue !== hour) {
      // set temp hours only when invoked by mouse click or selecting new value by keyboard
      setTempHourValue(hour);
    }
  };

  const handleSelectMinute = (minute: number, e: React.MouseEvent<HTMLElement>) => {
    if ((e && e.nativeEvent.detail && e.currentTarget.contains(document.activeElement)) || tempMinuteValue !== minute) {
      // set temp minutes only when invoked by mouse click or selecting new value by keyboard
      setTempMinuteValue(minute);
    }
  };

  const handleHourKeyDown = (currentValue: number) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && tempHourValue === currentValue) {
      handleSubmitTempValues();
    }

    // if ((e.key === 'Enter' || e.key === ' ') && tempHourValue === undefined) {
    //   handleSubmitTempValues();
    // }
  };

  const handleMinuteKeyDown = (currentValue: number) => (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && tempMinuteValue === currentValue) {
      handleSubmitTempValues();
    }
  };

  const renderOptionItems = ({
    isActive,
    label: optionLabel,
    onSelectHandler,
    onKeyDownHandler,
    tabIndex,
    value: optionValue,
  }: {
    isActive: boolean;
    label: string;
    onSelectHandler: (value: any, event: React.MouseEvent<HTMLElement>) => void;
    onKeyDownHandler: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
    tabIndex?: number;
    value: number;
  }) => {
    return (
      <TimePickerOption
        active={isActive}
        key={optionValue}
        onClick={onSelectHandler}
        onKeyDown={onKeyDownHandler}
        tabIndex={tabIndex}
        value={optionValue}
      >
        {optionLabel}
      </TimePickerOption>
    );
  };

  const renderHoursOptions = hoursOptions.map((hourOption, index) => {
    const currentValue = tempHourValue ?? (isValueEmpty ? undefined : valueHours);
    const isActive = hourOption.value === currentValue;
    const tabIndex = index === 0 ? 0 : -1;
    const selectedTabIndex = isActive ? 0 : -1;

    return renderOptionItems({
      ...hourOption,
      isActive,
      onKeyDownHandler: handleHourKeyDown(hourOption.value),
      onSelectHandler: handleSelectHour,
      tabIndex: currentValue !== undefined ? selectedTabIndex : tabIndex,
    });
  });

  const renderMinutesOptions = minutesOptions.map((minuteOption, index) => {
    const currentValue = tempMinuteValue ?? (isValueEmpty ? undefined : valueMinutes);
    const isActive = minuteOption.value === currentValue;
    const tabIndex = index === 0 ? 0 : -1;
    const selectedTabIndex = isActive ? 0 : -1;

    return renderOptionItems({
      ...minuteOption,
      isActive,
      onKeyDownHandler: handleMinuteKeyDown(minuteOption.value),
      onSelectHandler: handleSelectMinute,
      tabIndex: currentValue !== undefined ? selectedTabIndex : tabIndex,
    });
  });

  let labelContent;

  if (!containInvalidTempValues) {
    if (tempMinuteValue !== undefined || tempHourValue !== undefined) {
      labelContent = `${
        tempHourValue !== undefined ? formatTimeToTwoDigitString(tempHourValue) : formatTimeToTwoDigitString(valueHours)
      }:${
        tempMinuteValue !== undefined
          ? formatTimeToTwoDigitString(tempMinuteValue)
          : formatTimeToTwoDigitString(valueMinutes)
      }`;
    } else if (!isValueEmpty) {
      labelContent = convertTimeFromNumberToStringValue(value);
    }
  }

  const handleTimeChange = (newValue: LooseValue | React.ChangeEvent<HTMLInputElement>) => {
    let newDateTime = newValue as Date;

    if (
      newValue === null ||
      (typeof newValue === 'object' && !(newValue as React.ChangeEvent<HTMLInputElement>)?.target?.value)
    ) {
      /**
       * TimePicker invokes this handlers twice in a row, first time with event object,
       * second time with value only if it was able to parse some based on input
       */
      setTempHourValue(-1);
      setTempMinuteValue(-1);
    }

    if (typeof newValue === 'string') {
      newDateTime = parse(newValue, TIME_FORMAT_24_HOURS, new Date());
    }

    if (isValid(newDateTime)) {
      setTempHourValue(newDateTime.getHours());
      setTempMinuteValue(newDateTime.getMinutes());
    }
  };

  const onBlurHandler = (e: React.FocusEvent<HTMLElement>) => {
    setFocused(false);
    onBlur?.(e);
    onToggle(false);
  };

  const onFocusHandler = (e: React.FocusEvent<HTMLElement>) => {
    setFocused(true);
    onFocus?.(e);
    onToggle(true);
    setIsOpen(true);
  };

  const handleCancelChanges = () => {
    handleClearTempValues();
    setIsOpen(false);
    setFocused(false);
  };

  const handleSubmit = () => {
    handleSubmitTempValues();
    setIsOpen(false);
    setFocused(false);
  };

  const cancelButtonKeyDownHandler = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleCancelChanges();
    }
  };

  const submitButtonKeyDownHandler = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleSubmit();
    }
  };

  const onToggleClickHandler = () => {
    dropdownContentRef?.current?.querySelectorAll('input')?.[1]?.focus();
  };

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      if (containInvalidTempValues) {
        onSelect();
      } else {
        const [newHours, newMinutes] = getNearestValidOptions(
          tempHourValue ?? valueHours,
          tempMinuteValue ?? valueMinutes
        );
        const newTimeValue = newHours * ONE_HOUR_IN_SECONDS + newMinutes * ONE_MINUTE_IN_SECONDS;
        onSelect(newTimeValue);
      }

      handleClearTempValues();
      setIsOpen(!isOpen);
    }
    if (e.key === 'Escape') {
      setTempHourValue(undefined);
      setTempMinuteValue(undefined);
      if (isOpen) {
        setIsOpen(false);
      }
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      setTempHourValue(-1);
      setTempMinuteValue(-1);
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (!isOpen) {
        setIsOpen(true);
      }

      e.preventDefault();

      if (e.key === 'ArrowUp') {
        if ((e.target as HTMLInputElement)?.name === 'minute') {
          // increase by stepMinutes
          const newValueHours = tempHourValue ?? valueHours ?? 0;
          const newValueMinutes = (tempMinuteValue ?? valueMinutes ?? 0) + stepMinutes;
          const newValue = newValueHours * ONE_HOUR_IN_SECONDS + newValueMinutes * ONE_MINUTE_IN_SECONDS;

          if (newValue < max) {
            handleTimeChange(convertTimeFromNumberToStringValue(newValue));
          }
        } else {
          // increase by stepHours
          const newValueHours = (tempHourValue ?? valueHours ?? 0) + stepHours;
          const newValueMinutes = tempMinuteValue ?? valueMinutes ?? 0;
          const newValue = newValueHours * ONE_HOUR_IN_SECONDS + newValueMinutes * ONE_MINUTE_IN_SECONDS;

          if (newValue < max) {
            handleTimeChange(convertTimeFromNumberToStringValue(newValue));
          }
        }
      } else if ((e.target as HTMLInputElement)?.name === 'minute') {
        // decrease by stepMinutes
        const newValueHours = tempHourValue ?? valueHours ?? 0;
        const newValueMinutes = (tempMinuteValue ?? valueMinutes ?? 0) - stepMinutes;
        const newValue = newValueHours * ONE_HOUR_IN_SECONDS + newValueMinutes * ONE_MINUTE_IN_SECONDS;

        if (newValue >= min) {
          handleTimeChange(convertTimeFromNumberToStringValue(newValue));
        }
      } else {
        // decrease by stepHours
        const newValueHours = (tempHourValue ?? valueHours ?? 0) - stepHours;
        const newValueMinutes = tempMinuteValue ?? valueMinutes ?? 0;
        const newValue = newValueHours * ONE_HOUR_IN_SECONDS + newValueMinutes * ONE_MINUTE_IN_SECONDS;

        if (newValue >= min) {
          handleTimeChange(convertTimeFromNumberToStringValue(newValue));
        }
      }
    }
  };

  return (
    <Dropdown
      className={classnames('time-input', className)}
      data-test-id={dataTestId}
      isOpen={isOpen}
      onBlur={onBlurHandler}
      onFocus={onFocusHandler}
      toggle={handleToggle}
    >
      <div ref={dropdownContentRef}>
        <DropdownToggle
          caret
          className={classnames('time-input__toggler', 'btn', 'btn-info', {
            'time-input__toggler--active': active || isOpen || isFocused,
            'time-input__toggler--valid': valid && (dirty || modified || value),
            'time-input__toggler--invalid': !valid && (dirty || modified),
            'time-input__toggler--disabled': disabled,
          })}
          color="info"
          disabled={disabled ?? false}
          onClick={onToggleClickHandler}
          tag="div"
        >
          <div className="time-input__input-wrapper">
            <Label
              className={classnames('TextInputFloating', 'Label', {
                active: isOpen || labelContent || isFocused,
              })}
            >
              {label}
            </Label>
            <TimePicker
              className={classnames('react-time-picker__container', {
                'react-time-picker__container--hidden': !labelContent && !isOpen && !isFocused,
                'react-time-picker__container--disabled': disabled,
              })}
              clearIcon={null}
              disableClock
              format={TIME_FORMAT_24_HOURS}
              name={inputName}
              onChange={handleTimeChange}
              value={labelContent?.toString()}
              disabled={disabled ?? false}
              {...{
                // component will accept following props even though they are not listed in prop types
                onClick: (e: Event) => {
                  if (!isOpen) {
                    handleToggle();
                  }
                  e.stopPropagation();
                },
                onKeyDown: onKeyDownHandler,
              }}
            />
          </div>
          <Icon className="ml-2" kind="chevron-down" width={12} height={7} color="white" />
        </DropdownToggle>
        <DropdownMenu
          className="time-input__menu-wrapper"
          modifiers={{ preventOverflow: { boundariesElement: 'window' } }}
          positionFixed
          right={alignMenuToEnd}
        >
          <>
            <div className="d-flex">
              <div className="time-input__menu-section-container">
                <p className="time-input__menu-label">{t(`time.${minSecOption ? 'minute' : 'hour'}`, { count: 2 })}</p>
                <PerfectScrollbar
                  className="dropdown-menu-scroll w-100"
                  containerRef={setHoursContainerRef}
                  options={{
                    minScrollbarLength: 40,
                    wheelPropagation: false,
                  }}
                >
                  {renderHoursOptions}
                </PerfectScrollbar>
              </div>
              <Separator className="time-input__menu-section-separator" showBorder />
              <div className="time-input__menu-section-container">
                <p className="time-input__menu-label">
                  {t(`time.${minSecOption ? 'second' : 'minute'}`, { count: 2 })}
                </p>
                <PerfectScrollbar
                  className="dropdown-menu-scroll w-100"
                  containerRef={setMinutesContainerRef}
                  options={{
                    minScrollbarLength: 40,
                    wheelPropagation: false,
                  }}
                >
                  {renderMinutesOptions}
                </PerfectScrollbar>
              </div>
            </div>
            <div className="time-input__menu-buttons-container">
              <Button
                className="form-control"
                kind="link"
                onClick={handleCancelChanges}
                onKeyDown={cancelButtonKeyDownHandler}
                role="menuitem"
              >
                <Icon className="mr-2" color="orange" kind="cross" size={16} />
                {t('form.cancel')}
              </Button>
              <Button
                className="form-control"
                kind="link"
                onClick={handleSubmit}
                onKeyDown={submitButtonKeyDownHandler}
                role="menuitem"
              >
                <Icon kind="checkmark" color="lightgreen" size={16} className="mr-2" />
                {t('form.set')}
              </Button>
            </div>
          </>
        </DropdownMenu>
      </div>
    </Dropdown>
  );
};

export interface SelectFieldProps {
  alignMenuToEnd?: boolean;
  'data-test-id'?: string;
  max?: number;
  min?: number;
  name: string;
  label: string;
  validate?: TValidator;
  disabled?: boolean;
  className?: string;
  stepMinutes?: number;
  stepHours?: number;
  minSecOption?: boolean;
}

export const TimeDurationPickerField: React.FC<SelectFieldProps> = ({ className, name, validate, ...restProps }) => {
  return (
    <Field name={name} validate={validate}>
      {({ input: { value, onChange }, meta }) => (
        <TimeDurationPicker
          {...restProps}
          className={classnames(className, 'time-duration-select')}
          inputName={name}
          meta={meta}
          onSelect={(newValue?: number) => onChange(newValue)}
          value={value}
        />
      )}
    </Field>
  );
};
